import React, { useState, useEffect } from 'react';
import { HealthStatus } from '../types';
import ApiService from '../services/api';

const HealthCheck: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        setLoading(true);
        const data = await ApiService.getHealthStatus();
        setHealth(data);
        setError(null);
      } catch (error: any) {
        console.error('Error fetching health data:', error);
        // Provide more specific error messages
        if (error.status === 404) {
          setError('Health endpoint not found. Please check if the API server is running.');
        } else if (error.status === 401 || error.status === 403) {
          setError('Authentication required for health check. Please login first.');
        } else if (error.message && error.message.includes('fetch')) {
          setError('Cannot connect to API server. Please check if the server is running.');
        } else {
          setError('Failed to fetch health data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
    
    // Refresh health data every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return '#10b981';
      case 'degraded':
        return '#f59e0b';
      case 'down':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return '✅';
      case 'degraded':
        return '⚠️';
      case 'down':
        return '❌';
      default:
        return '❓';
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error && !health) {
    return (
      <div>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#212529',
            marginBottom: '8px'
          }}>
            System Health Check
          </h2>
          <p style={{ color: '#495057', fontSize: '16px', margin: 0 }}>
            Monitor the status of WHOIS API services and system components.
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            padding: '24px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>Health Check Failed</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', lineHeight: '1.5' }}>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Retry Health Check
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Overall Status */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '8px'
            }}>
              Overall System Status
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
              Last updated: {health ? new Date(health.last_check).toLocaleString() : ''}
            </p>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: health?.status === 'healthy' ? '#f0fdf4' : '#fef2f2',
            padding: '12px 20px',
            borderRadius: '8px'
          }}>
            <span style={{ fontSize: '20px', marginRight: '8px' }}>
              {getStatusIcon(health?.status || '')}
            </span>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: getStatusColor(health?.status || ''),
              textTransform: 'capitalize'
            }}>
              {health?.status}
            </span>
          </div>
        </div>
      </div>

      {/* Services Status */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef',
        marginBottom: '24px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '16px'
        }}>
          Service Components
        </h3>
        <div style={{ display: 'grid', gap: '16px' }}>
          {health && health.services && [
            { key: 'whois_service', name: 'WHOIS Service', description: 'WHOIS protocol service for domain lookups', status: health.services.whois_service },
            { key: 'rdap_service', name: 'RDAP Service', description: 'RDAP protocol service for modern domain queries', status: health.services.rdap_service },
            { key: 'database', name: 'Database', description: 'Database connectivity and operations', status: health.services.database }
          ].map(({ key, name, description, status }) => (
            <div key={key} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #f3f4f6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '20px', marginRight: '12px' }}>
                  {status ? '✅' : '❌'}
                </span>
                <div>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 4px 0'
                  }}>
                    {name}
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0
                  }}>
                    {description}
                  </p>
                </div>
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: status ? '#10b981' : '#ef4444',
                backgroundColor: status ? '#f0fdf4' : '#fef2f2',
                padding: '4px 12px',
                borderRadius: '6px'
              }}>
                {status ? 'Operational' : 'Down'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      {health && health.performance && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '16px'
          }}>
            Performance Metrics
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <div style={{
              padding: '16px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ fontSize: '14px', color: '#065f46', fontWeight: '600', marginBottom: '4px' }}>
                API Latency
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#047857' }}>
                {health.performance.api_latency_ms?.toFixed(1) || 'N/A'}ms
              </div>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>
                WHOIS Test
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#1d4ed8' }}>
                {health.performance.whois_test_latency_ms?.toFixed(1) || 'N/A'}ms
              </div>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #fde68a'
            }}>
              <div style={{ fontSize: '14px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>
                RDAP Test
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#d97706' }}>
                {health.performance.rdap_test_latency_ms?.toFixed(1) || 'N/A'}ms
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Statistics */}
      {health && health.statistics && (
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '16px'
          }}>
            System Statistics
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px'
          }}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                {health.statistics.total_requests?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                Total Requests
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                {health.statistics.active_tokens || '0'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                Active Tokens
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                {health.statistics.supported_tlds?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                Supported TLDs
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                {health.statistics.uptime_seconds ? Math.floor(health.statistics.uptime_seconds / 3600) + 'h' : '0h'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                Uptime
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthCheck; 